import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, getToken } from "./client";

export const useHome = () =>
    useQuery({
        queryKey: ["home"],
        queryFn: async () => (await api.get("/home")).data,
        staleTime: 60_000,
    });

export const useOffers = (params) =>
    useQuery({
        queryKey: ["offers", params || {}],
        queryFn: async () => (await api.get("/offers", { params })).data,
        staleTime: 60_000,
    });

export const useOffer = (id) =>
    useQuery({
        queryKey: ["offer", id],
        queryFn: async () => (await api.get(`/offers/${id}`)).data,
        enabled: Boolean(id),
    });

export const useBrands = () =>
    useQuery({
        queryKey: ["brands"],
        queryFn: async () => (await api.get("/brands")).data,
        staleTime: 300_000,
    });

export const useBrand = (id) =>
    useQuery({
        queryKey: ["brand", id],
        queryFn: async () => (await api.get(`/brands/${id}`)).data,
        enabled: Boolean(id),
    });

export const useCategories = () =>
    useQuery({
        queryKey: ["categories"],
        queryFn: async () => (await api.get("/categories")).data,
        staleTime: 300_000,
    });

export const useSearch = (q) =>
    useQuery({
        queryKey: ["search", q],
        queryFn: async () => (await api.get("/search", { params: { q } })).data,
        enabled: Boolean(q),
    });

export const useAiSearch = () =>
    useMutation({
        mutationFn: async (query) => (await api.post("/ai-search", { query })).data,
    });

export const useProductSearch = () =>
    useMutation({
        mutationFn: async (payload) => (await api.post("/products/search", payload)).data,
    });

export const useFlightParse = () =>
    useMutation({
        mutationFn: async (text) => (await api.post("/travel/flights/parse", { text })).data,
    });

export const useFlightSearch = () =>
    useMutation({
        mutationFn: async (payload) => (await api.post("/travel/flights/search", payload)).data,
    });

export const useFlightBookingOptions = () =>
    useMutation({
        mutationFn: async (payload) =>
            (await api.post("/travel/flights/booking-options", payload)).data,
    });

export const useHotelCompare = () =>
    useMutation({
        mutationFn: async (payload) => (await api.post("/travel/hotels/compare", payload)).data,
    });

export const useHotelOffers = () =>
    useMutation({
        mutationFn: async (payload) => (await api.post("/travel/hotels/offers", payload)).data,
    });

export const useSaved = () =>
    useQuery({
        queryKey: ["saved"],
        queryFn: async () => (await api.get("/saved")).data,
        enabled: Boolean(getToken()),
        staleTime: 30_000,
    });

export const useToggleSave = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ kind, id, save }) =>
            (await (save ? api.post(`/saved/${kind}/${id}`) : api.delete(`/saved/${kind}/${id}`))).data,
        onSettled: () => qc.invalidateQueries({ queryKey: ["saved"] }),
    });
};

export const useRecommended = () =>
    useQuery({
        queryKey: ["recommended"],
        queryFn: async () => (await api.get("/recommended")).data,
        enabled: Boolean(getToken()),
        staleTime: 60_000,
    });

export const useActivity = () =>
    useQuery({
        queryKey: ["activity"],
        queryFn: async () => (await api.get("/activity")).data,
        enabled: Boolean(getToken()),
        staleTime: 30_000,
    });

export const useCardCatalogue = () =>
    useQuery({
        queryKey: ["card-catalogue"],
        queryFn: async () => (await api.get("/card-offers/catalogue")).data,
        staleTime: 3600_000,
    });

export const useCardOfferSearch = () =>
    useMutation({
        mutationFn: async (payload) => (await api.post("/card-offers/search", payload)).data,
    });

export const useTranscribe = () =>
    useMutation({
        mutationFn: async (file) => {
            const fd = new FormData();
            fd.append("file", file);
            return (await api.post("/transcribe", fd, { timeout: 90000 })).data;
        },
    });

export const useDeleteAccount = () =>
    useMutation({
        mutationFn: async (payload) => (await api.post("/account/delete", payload)).data,
    });

export const usePreferences = () =>
    useQuery({
        queryKey: ["preferences"],
        queryFn: async () => (await api.get("/preferences")).data,
        enabled: Boolean(getToken()),
    });

export const useUpdatePreferences = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => (await api.put("/preferences", payload)).data,
        onSuccess: (data) => qc.setQueryData(["preferences"], data),
    });
};

export const useUpdateProfile = () =>
    useMutation({
        mutationFn: async (payload) => (await api.put("/profile", payload)).data,
    });

export const useNotifications = () =>
    useQuery({
        queryKey: ["notifications"],
        queryFn: async () => (await api.get("/notifications")).data,
        enabled: Boolean(getToken()),
        staleTime: 30_000,
    });

export const useIntelCoupons = () =>
    useQuery({
        queryKey: ["intel-coupons"],
        queryFn: async () => (await api.get("/intel-coupons")).data,
        staleTime: 60_000,
    });

export const useUnreadCount = () =>
    useQuery({
        queryKey: ["notifications", "unread"],
        queryFn: async () => (await api.get("/notifications/unread-count")).data,
        enabled: Boolean(getToken()),
        refetchInterval: 60_000,
    });

export const useMarkRead = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id) => (await api.post(`/notifications/${id}/read`)).data,
        onSettled: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
    });
};

export const useMarkAllRead = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async () => (await api.post("/notifications/read-all")).data,
        onSettled: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
    });
};
